import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { parse, stringify } from 'querystring';
import { FormattedMessage } from 'react-intl';
import { ChevronRight, Calendar } from 'react-feather';
import { path, propOr } from 'ramda';
import PropTypes from 'prop-types';
import moment from 'moment';

import Modal from 'components/ui/Modal';
import { Row, Column } from 'components/shared/Layout';
import { Spinner } from 'admin-components/components';
import { ActionButton } from 'components/ui/Button';
import AlertBox from 'components/ui/AlertBox';
import FareIndicator from 'components/ui/FareIndicator';
import CurrencySelect from 'components/shared/CurrencySelect';
import ApolloErrorSection from 'components/errors/ApolloErrorSection';
import StyledSelect from 'components/ui/StyledReactSelect';
import JourneySearchHeader from 'components/shared/JourneySearchHeader';
import JourneySearchTables from 'components/shared/JourneySearchTables';
import JourneyDigest from 'components/shared/JourneyDigest';

import { productFamilyCodes } from 'types/productFamilyCodes';
import { types as paxTypes } from 'types/passengers';
import { historyShape, searchParamsShape, locationShape, stationsShape, clientShape, userShape } from 'constants/propTypes';
import { brandCode } from 'constants/brandCodes';
import { isDBLeg } from 'utils/segmentTypes';
import { ACTION_CODES } from 'constants/errors';
import AgentPortalError from 'errors/AgentPortalError';
import { removeEmptyValues, formatProductFamilyName } from 'utils/functions';
import { getOfflineFees } from 'utils/exchange';
import SearchRequestBookingMagnet from 'components/shared/SearchRequestBookingMagnet';
import { isUserEurostarAgent } from 'utils/user';

import discountCardsMap from 'types/discountCardsMap';
import { ADD_ADDITIONAL_DETAILS } from 'store/mutations';
import { MOBILE_TERMINAL_ID_STORAGE_KEY } from 'constants/user';
import {
  buildSegments,
  buildOutboundJourneys,
  buildInboundJourneys,
  buildLegsFromFare,
  hasProductFamilyMismatch,
  getTotalPrice,
  buildInboundInfo,
  calcSegmentPointsTotal,
  isDogOnlySearch,
  sendSNCBDBOrNSAge
} from './JourneySearch.utils';

import {
  StyledHorizontalRule,
  StyledPage,
  ConfirmButton,
  StyledToggleButton,
  Grid,
  AvailablePointsCell,
  AvailablePointsBoldCell,
  ClubEurostarImg,
  Error,
  HereLink,
  StyledFareTypeContainer
} from './JourneySearch.styles';
import { GET_CUSTOMER_DETAILS, GET_PROFILE } from '../../store/queries';

import { THALYS_OFFICES } from 'types/constants';
import { isLoyaltyTier3OrAbove } from 'utils/loyaltyData';
import { getProductFamilyPath } from 'utils/isGroupBooking';
import { searchTypes } from 'pages/BookingMagnet/BookingMagnet.constants';
import groupProductFamilies from 'constants/groupProductFamilies';
import { getInfantCountFromSearch } from 'utils/getInfantPassengersFromDetails';
import { isSNCBUnconfirmedTimetableBookingExchangeEnabled } from 'utils/featureFlags';
import { hasSNCBUnconfirmedTimetableSearchResults } from 'utils/sncbUnconfirmedTimetable';
import { getEvacAssistItemRefs } from 'utils/booking';

const LANGUAGES = {
  'uk-en': 'EN',
  'fr-fr': 'FR',
  'nl-nl': 'NL',
  'de-de': 'DE'
};

const initialState = {
  outbound: null,
  inbound: null,
  showBookingMagnet: false,
  isMember: false,
  activePoints: 0,
  showNotEnoughPoints: false,
  hasSpecialLoyaltyTier: false,
  selectedProductFamily: undefined,
  uniqProductFamiliesWithFares: []
};

const JourneySearch = (props) => {
  const {
    client,
    membershipNumber,
    searchParams,
    history: {
      push,
      location: { search, pathname, hash, state }
    },
    data,
    error,
    loading,
    stations,
    createProvisionalBooking,
    user,
    cheapestFares,
    animalsPerTrain,
    animalsPerTrainLoading,
    serviceDetailsData,
    serviceDetailsLoading,
    assistancePerTrain,
    assistancePerTrainLoading,
    isDesktop,
    seniorsAges,
    youthAges,
    childAges
  } = props;

  const [journeySearchState, setJourneySearchState] = useState(initialState);

  const {
    outbound,
    inbound,
    showBookingMagnet,
    isMember,
    activePoints,
    showNotEnoughPoints,
    selectedProductFamily,
    uniqProductFamiliesWithFares
  } = journeySearchState;

  const setState = useCallback((nextState) => {
    setJourneySearchState((prevState) => ({
      ...prevState,
      ...(typeof nextState === 'function' ? nextState(prevState) : nextState)
    }));
  }, []);

  const errorParentRef = useRef({
    props: { history: { location: { pathname, search, hash }, push } },
    setState: () => {}
  });

  useEffect(() => {
    errorParentRef.current = {
      props: {
        history: {
          push,
          location: {
            pathname,
            search,
            hash,
            state
          }
        }
      },
      setState: (parentState) => {
        setState(parentState);
      }
    };
  }, [pathname, push, search, hash, state, setState]);

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      if (!membershipNumber) {
        return;
      }

      const result = await client.query({
        query: GET_CUSTOMER_DETAILS,
        variables: {
          membershipNumber
        }
      });

      const customerInfo = result?.data?.getCustomerDetails;
      if (customerInfo?.profile?.loyalty) {
        setState({
          isMember: true,
          activePoints: customerInfo.profile.loyalty?.rewardPoints,
          hasSpecialLoyaltyTier: isLoyaltyTier3OrAbove(customerInfo.profile?.loyalty?.tier.tierCode ?? '')
        });

        const subscriptionStartDate = new URLSearchParams(search).get('subscriptionStartDate');
        const subscriptionEndDate = new URLSearchParams(search).get('subscriptionEndDate');

        if (subscriptionStartDate && subscriptionEndDate) {
          setState({
            subscriptionStartDate,
            subscriptionEndDate
          });
        } else {
          const subscriptionID = new URLSearchParams(search).get('subscriptionID');

          if (subscriptionID) {
            try {
              const profileResult = await client.query({
                query: GET_PROFILE,
                variables: {
                  customerId: customerInfo?.profile?.customerId,
                  subscriptionSearchType: 'CURRENT'
                }
              });

              const subscription = profileResult?.data?.profile.subscriptions.find((s) => s.travelPassReference === subscriptionID);
              if (subscription) {
                setState({
                  subscriptionStartDate: subscription?.startDate,
                  subscriptionEndDate: subscription?.endDate
                });
              }
            } catch (err) {
              setState({
                subscriptionStartDate: undefined,
                subscriptionEndDate: undefined
              });
            }
          }
        }
      }
    };

    fetchCustomerDetails();
  }, [client, membershipNumber, search, setState]);

  const prevDataRef = useRef();
  useEffect(() => {
    if (prevDataRef.current === data) {
      return;
    }
    prevDataRef.current = data;

    const isNewSearchMode = searchParams.productFamilies.length === 0;

    if (isNewSearchMode) {
      const defaultProductFamily =
        searchParams.bookingTypeSelected === searchTypes.GROUP ? productFamilyCodes.GROUP : productFamilyCodes.PUB;

      const { outbound: searchOutbound, inbound: searchInbound } = data.journeySearch;

      const groupFamilies = new Set(groupProductFamilies);

      const combinedProductFamilies = [
        ...(searchOutbound?.productFamiliesWithFares ?? []),
        ...(searchInbound?.productFamiliesWithFares ?? [])
      ];

      const familyGroupMap = new Map();

      combinedProductFamilies.forEach(({ familyGroup, familyGroupName }) => {
        if (searchParams.bookingTypeSelected === searchTypes.GROUP ? groupFamilies.has(familyGroup) : !groupFamilies.has(familyGroup)) {
          if (!familyGroupMap.has(familyGroup)) {
            familyGroupMap.set(familyGroup, familyGroupName);
          }
        }
      });

      const nextProductFamilies = Array.from(familyGroupMap).map(([value, label]) => ({ value, label }));

      const nextSelectedProductFamily =
        nextProductFamilies.find(({ value }) => value === defaultProductFamily)?.value || nextProductFamilies[0]?.value;

      setState({
        uniqProductFamiliesWithFares: nextProductFamilies,
        selectedProductFamily: nextSelectedProductFamily
      });
    }
  }, [data, searchParams, setState]);

  useEffect(() => {
    const { outboundDate, inboundDate } = parse(search.substr(1));

    const currentOutboundDate = outbound && outbound.date;
    const currentInboundDate = inbound && inbound.date;

    if (
      (!!outboundDate && !!currentOutboundDate && outboundDate !== currentOutboundDate) ||
      (!!inboundDate && !!currentInboundDate && inboundDate !== currentInboundDate)
    ) {
      setState(initialState);
      return;
    }

    if (moment(outboundDate).isAfter(inbound?.legs[0]?.timing?.departureDate)) {
      setState({ inbound: null });
    }
  }, [search, outbound, inbound, setState]);

  const isReturnJourney = useMemo(() => !!path(['searchParams', 'inboundDate'], { searchParams }), [searchParams]);

  const offlineFee = useMemo(() => {
    const { bookingTypeSelected } = searchParams;
    const { membershipNumber, subscriptionID, subscriptionCode } = parse(search.substr(1));
    const isSubscriptionTicket = membershipNumber && subscriptionID && subscriptionCode;
    const isThalysUser = user?.offices?.filter((office) => THALYS_OFFICES.includes(office.name))?.length > 0;
    const isPackageBooking = state?.isPackageBooking;

    return getOfflineFees({
      outbound,
      inbound,
      bookingTypeSelected,
      isSubscriptionTicket,
      isThalysUser,
      hasSpecialLoyaltyTier: journeySearchState.hasSpecialLoyaltyTier,
      isPackageBooking
    });
  }, [outbound, inbound, searchParams, search, user, journeySearchState.hasSpecialLoyaltyTier, state]);

  const getDiscountCards = useCallback(
    (type) => {
      const { productFamilies } = searchParams;
      const { subscriptionID, subscriptionCode, membershipNumber: membershipNumberFromSearch } = parse(search.substr(1));

      const discountCards = [];

      if (membershipNumberFromSearch && subscriptionID && subscriptionCode) {
        discountCards.push({
          code: subscriptionCode.startsWith('TH_FP') ? productFamilyCodes.THPASSFP : productFamilyCodes.THPASSPR,
          identifier: subscriptionID
        });

        if (type !== paxTypes.CHILD) {
          discountCards.push({ code: productFamilyCodes.EUROSTAR_LOYALTY, identifier: membershipNumberFromSearch });
        }
      }

      if (productFamilies) {
        productFamilies.forEach((product) => {
          if (discountCardsMap[product]) {
            discountCards.push(discountCardsMap[product]);
          }
        });
      }

      if (selectedProductFamily && !discountCards.some((card) => card.code === selectedProductFamily)) {
        if (discountCardsMap[selectedProductFamily]) {
          discountCards.push(discountCardsMap[selectedProductFamily]);
        }
      }

      return discountCards;
    },
    [searchParams, search, selectedProductFamily]
  );

  const toggleSearchMenu = useCallback(() => {
    setState((prev) => ({
      showBookingMagnet: !prev.showBookingMagnet
    }));
  }, [setState]);

  const toggleNotEnoughPointsModal = useCallback(() => {
    setState((prev) => ({
      showNotEnoughPoints: !prev.showNotEnoughPoints
    }));
  }, [setState]);

  const selectFare = useCallback(
    (fareObj) => {
      const { direction } = fareObj;

      setState({
        [direction]: fareObj
      });
    },
    [setState]
  );

  const selectCurrency = useCallback(
    (currencyValue) => {
      const searchObj = parse(search.substr(1));

      setState(initialState);
      push({
        pathname: '/journeys',
        search: stringify({
          ...searchObj,
          currency: currencyValue
        }),
        state
      });
    },
    [search, push, state, setState]
  );

  const hasEnoughPoints = useCallback(() => {
    const outboundTotalPoints = calcSegmentPointsTotal(outbound);
    const inboundTotalPoints = calcSegmentPointsTotal(inbound);

    if (isMember && searchParams.productFamilies.includes(productFamilyCodes.EUROSTAR_LOYALTY)) {
      if ((outboundTotalPoints || 0) + (inboundTotalPoints || 0) > activePoints) {
        setState({
          showNotEnoughPoints: true
        });

        return false;
      }
    }

    return true;
  }, [activePoints, isMember, outbound, inbound, searchParams.productFamilies, setState]);

  const hasDBOrNSOrLyriaOrSNCFLeg = useCallback(() => {
    if (!outbound && !inbound) {
      return false;
    }

    return (
      (outbound?.isSummatedFare || inbound?.isSummatedFare) &&
      (outbound?.legs?.some(
        (leg = {}) =>
          isDBLeg(leg) || [brandCode?.SNCF, brandCode?.INOUI, brandCode?.Lyria, brandCode?.NS].includes(leg?.serviceType?.brandCode)
      ) ||
        inbound?.legs?.some(
          (leg = {}) =>
            isDBLeg(leg) || [brandCode?.SNCF, brandCode?.INOUI, brandCode?.Lyria, brandCode?.NS].includes(leg?.serviceType?.brandCode)
        ))
    );
  }, [outbound, inbound]);

  const isPackageBooking = state?.isPackageBooking;

  const handleSubmit = useCallback(
    ({ isProvisionalDefault = false } = {}) =>
      async (e) => {
        e.preventDefault();

        if (!hasEnoughPoints()) {
          return;
        }

        const passengers = data.journeySearch.passengers;
        const isEvac = searchParams.adultsEvac > 0 || searchParams.childrenEvac > 0;

        const segments = buildSegments(
          {
            outbound,
            inbound
          },
          isReturnJourney,
          isPackageBooking,
          isEvac
        );

        if (offlineFee) {
          segments.push({
            startValidityDate: moment().format('YYYY-MM-DD'),
            items: [
              {
                passengerId: offlineFee.passengerId,
                tariffCode: offlineFee.tariffCode,
                price: offlineFee.price
              }
            ]
          });
        }

        const bookingData = await createProvisionalBooking({
          variables: {
            fulfillmentMethodCode: 'PAH',
            currency: searchParams.currency,
            segments,
            passengers: passengers.map(({ id, type, disabilityType, birthDate }) =>
              removeEmptyValues({
                id,
                type,
                birthDate,
                age: sendSNCBDBOrNSAge({
                  id,
                  type,
                  containSNCBStation: searchParams.containSNCBStation,
                  containDBStation: searchParams.containDBStation,
                  containNSStation: searchParams.containNSStation,
                  outbound,
                  inbound,
                  seniorsAges: passengers
                    .filter((passenger) => passenger.type === paxTypes.SENIOR)
                    ?.map((pax, index) => ({
                      id: pax.id,
                      age: seniorsAges?.[index]
                    })),
                  youthAges: youthAges?.length
                    ? passengers
                        .filter((passenger) => passenger.type === paxTypes.YOUTH)
                        ?.map((pax, index) => ({
                          id: pax.id,
                          age: youthAges?.[index]
                        }))
                    : [],
                  childAges: childAges?.length
                    ? passengers
                        .filter((passenger) => passenger.type === paxTypes.CHILD)
                        ?.map((pax, index) => ({
                          id: pax.id,
                          age: childAges?.[index]
                        }))
                    : []
                }),
                disabilityType,
                discountCards: getDiscountCards(type)
              })
            ),
            contractCode: searchParams.contractCode,
            language: LANGUAGES[new URLSearchParams(search).get('language')] || 'EN'
          },
          isPackageBooking,
          onCompleted: ({ createBooking: { reference, productFamilyGroup, outboundExtras, inboundExtras } }) => {
            if (isEvac) {
              const bookingProducts = getEvacAssistItemRefs([...outboundExtras, ...inboundExtras]);

              bookingProducts.map(
                async (item) =>
                  await client.mutate({
                    mutation: ADD_ADDITIONAL_DETAILS,
                    variables: {
                      bookingReference: reference,
                      additionalDetails: [
                        {
                          key: 'MAAS',
                          value: 'Evac chair',
                          itemRefId: Number(item)
                        }
                      ]
                    }
                  })
              );
            }

            if (isProvisionalDefault) {
              push(`/manage/bookings/${reference}/journeys`);
            } else {
              const type = getProductFamilyPath(productFamilyGroup);
              const searchParams = new URLSearchParams(search);
              const membershipNumber = searchParams.get('membershipNumber');
              const subscriptionID = searchParams.get('subscriptionID');
              const subscriptionCode = searchParams.get('subscriptionCode');
              const infants = searchParams.get('infants');

              const params = new URLSearchParams();
              if (membershipNumber) {
                params.set('membershipNumber', membershipNumber);
                if (subscriptionID) {
                  params.set('subscriptionID', subscriptionID);
                  params.set('subscriptionCode', subscriptionCode);
                }
              }
              if (infants && Number(infants) > 0) params.set('infants', infants);
              const queryString = params.toString();

              push(`/booking/${reference}/${type}${queryString ? `?${queryString}` : ''}`, state);
            }
          }
        });

        if (user && user.isStation && localStorage.getItem(MOBILE_TERMINAL_ID_STORAGE_KEY)) {
          await client.mutate({
            mutation: ADD_ADDITIONAL_DETAILS,
            variables: {
              bookingReference: bookingData.data.createBooking.reference,
              additionalDetails: [
                {
                  key: `tablet-create-${moment().format('HH:mm:ss.DDMMYY')}`,
                  value: user.paymentLocation && user.paymentLocation.name
                }
              ]
            }
          });
        }
      },
    [
      createProvisionalBooking,
      client,
      data,
      getDiscountCards,
      hasEnoughPoints,
      isPackageBooking,
      isReturnJourney,
      offlineFee,
      outbound,
      inbound,
      push,
      search,
      searchParams,
      seniorsAges,
      youthAges,
      childAges,
      state,
      user
    ]
  );

  const handleSearchChange = useCallback(
    (searchData) => {
      setState({ showBookingMagnet: false });

      const { travelDate, returnDate } = searchData;
      const existingQuery = parse(search.substr(1));
      const outboundDate = existingQuery.outboundDate && travelDate;
      const inboundDate = (outboundDate && returnDate) || (existingQuery.inboundDate && travelDate);

      const searchParams = removeEmptyValues({
        ...existingQuery,
        outboundDate,
        inboundDate
      });

      push({
        pathname: `/journeys`,
        search: stringify(searchParams),
        state: state
      });
    },
    [search, push, state, setState]
  );

  const handleSetProductFamily = useCallback(
    ({ value }) => {
      setState({
        outbound: null,
        inbound: null,
        selectedProductFamily: value
      });
    },
    [setState]
  );

  const {
    totalPrice = 0,
    outboundTotalPrice = 0,
    inboundTotalPrice = 0
  } = getTotalPrice({
    outbound: outbound || {},
    inbound: inbound || {},
    fees: [offlineFee ? offlineFee.price : 0]
  });

  const isEsUser = isUserEurostarAgent(user);
  const dogOnlySearch = isDogOnlySearch({ searchParams });

  const {
    adultsWheelchair,
    childrenWheelchair,
    wheelchairCompanions,
    outboundDate,
    inboundDate,
    productFamilies,
    currency,
    adultsEvac,
    childrenEvac
  } = searchParams;

  const searchWithoutQuestionMark = search.substr(1);
  const { originStationName, destinationStationName, returningOriginStationName, returningDestinationStationName } =
    parse(searchWithoutQuestionMark);

  const isWheelchairSearch = adultsWheelchair > 0 || childrenWheelchair > 0 || wheelchairCompanions > 0;
  const isLoyaltyBooking = productFamilies.includes(productFamilyCodes.EUROSTAR_LOYALTY);
  const isLoyaltyLoggedOut = isLoyaltyBooking && !membershipNumber;
  const isEvacSearch = adultsEvac > 0 || childrenEvac > 0;
  const isUnassignedSearch = productFamilies.includes(productFamilyCodes.RED_US);

  const isSubmitDisabled = useCallback(() => {
    const returnJourney = !!inboundDate;

    if (returnJourney) {
      return !outbound || !inbound || isLoyaltyLoggedOut;
    }

    return !outbound || isLoyaltyLoggedOut;
  }, [inbound, inboundDate, isLoyaltyLoggedOut, outbound]);

  const outboundInfo = {
    date: outboundDate,
    origin: originStationName,
    destination: destinationStationName,
    originUIC: searchParams.origin,
    destinationUIC: searchParams.destination
  };

  const inboundInfo = buildInboundInfo({
    date: inboundDate,
    returningOriginStationName,
    returningDestinationStationName,
    destinationStationName,
    originStationName,
    originUIC: searchParams.origin,
    destinationUIC: searchParams.destination,
    returningOriginUIC: searchParams.returningOrigin,
    returningDestinationUIC: searchParams.returningDestination
  });

  const searchMenuTextId = showBookingMagnet ? 'exchange.hideBookingMagnet' : 'exchange.showBookingMagnet';

  const handleRedirectToClubEurostar = useCallback(() => {
    push(`/clubeurostar/manage`, {
      prevPath: pathname,
      prevSearch: search,
      prevHash: hash
    });
  }, [pathname, push, search, hash]);

  if (loading) {
    return (
      <Row span={16} height="100vh">
        <Column justifyContent="center" alignItems="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  if (error) {
    return (
      <>
        <JourneySearchHeader outbound={outboundInfo} inbound={inboundInfo} />
        <StyledHorizontalRule />
        <Row>
          <ApolloErrorSection error={new AgentPortalError(error, ACTION_CODES.BACK_TO_BOOKING_MAGNET)} parent={errorParentRef.current} />
        </Row>
      </>
    );
  }

  const { passengers, productFamiliesWithFares, outbound: journeyOutbound, inbound: journeyInbound } = data.journeySearch;

  const isGroupBooking = (productFamilies?.length > 0 ? productFamilies : productFamiliesWithFares).includes(productFamilyCodes.GROUP);
  const getBookingItems = propOr([], 'bookingItems');
  const hasJourneys = getBookingItems(outbound).length > 0 || getBookingItems(inbound).length > 0;

  const hasMismatch = hasProductFamilyMismatch(productFamilies, productFamiliesWithFares);
  const showSNCBUnconfirmedTimetableWarning =
    isSNCBUnconfirmedTimetableBookingExchangeEnabled() && hasSNCBUnconfirmedTimetableSearchResults(data.journeySearch);

  const uniqueFamilyGroupNames = [...new Set(productFamiliesWithFares.map((pf) => pf.familyGroupName))];
  const allProductFamiliesMessage = uniqueFamilyGroupNames.map((name) => formatProductFamilyName(name)).join(', ');

  const outboundTotalPoints = calcSegmentPointsTotal(outbound);
  const inboundTotalPoints = calcSegmentPointsTotal(inbound);

  const searchUrl = membershipNumber ? `/search?membershipNumber=${membershipNumber}` : '/search';

  const loyaltyError = isLoyaltyLoggedOut ? (
    <Error id="error-not-loyalty-account">
      <FormattedMessage
        id="errorBoundary.notLoggedInLoyalty"
        values={{
          here: (
            <HereLink onClick={handleRedirectToClubEurostar}>
              <FormattedMessage id="errorBoundary.linkHere" />
            </HereLink>
          )
        }}
      />
    </Error>
  ) : null;

  return (
    <StyledPage columns={12}>
      <Row columns={12}>
        <Column start={0} span={1} justifyContent="flex-start" alignItems="center">
          <CurrencySelect
            defaultValue={currency}
            onSelect={selectCurrency}
            isPackageBooking={state?.isPackageBooking}
            isGroupBooking={isGroupBooking}
          />
        </Column>
        {selectedProductFamily && (
          <Column span={4} start={2} justifyContent="flex-start" alignItems="center">
            <StyledFareTypeContainer>
              <FormattedMessage id="bookingMagnet.fareType" tagName="span" />
              <StyledSelect
                id="fare-select"
                name="fare-select"
                data-testid="fare-select"
                data-cy="fare-select"
                className="react-select-container"
                classNamePrefix="react-select"
                width="200px"
                height="34px"
                options={uniqProductFamiliesWithFares}
                value={uniqProductFamiliesWithFares.find(({ value }) => value === selectedProductFamily)}
                onChange={handleSetProductFamily}
              />
            </StyledFareTypeContainer>
          </Column>
        )}
        <Column start={12} span={2} justifyContent="flex-end">
          <ActionButton
            data-cy="booking-path-new-search"
            onClick={() => {
              push(searchUrl);
            }}
          >
            <FormattedMessage id="searchResults.newSearch" />
          </ActionButton>
        </Column>
        <Column span={3} start={14} justifyContent="flex-end">
          <StyledToggleButton onClick={toggleSearchMenu} id="toggle-search">
            <Calendar />
            <FormattedMessage id={searchMenuTextId} />
          </StyledToggleButton>
        </Column>
      </Row>

      {hasMismatch && (
        <Row columns={12}>
          <AlertBox variant={AlertBox.variants.WARNING}>
            <FormattedMessage
              id="journeySearch.multipleProductFamilies"
              values={{
                actualProductFamilies: allProductFamiliesMessage,
                indicator: <FareIndicator data-cy="fare-indicator" />
              }}
            />
          </AlertBox>
        </Row>
      )}

      {showSNCBUnconfirmedTimetableWarning && (
        <Row columns={12}>
          <AlertBox variant={AlertBox.variants.WARNING} dataCy="sncb-unconfirmed-timetable-warning">
            <FormattedMessage id="sncbUnconfirmedTimetable.searchWarning" />
          </AlertBox>
        </Row>
      )}

      <Modal isOpen={showBookingMagnet} onClose={toggleSearchMenu}>
        <SearchRequestBookingMagnet
          origin={outboundInfo.originUIC}
          destination={outboundInfo.destinationUIC}
          returningOrigin={inboundInfo && inboundInfo.originUIC}
          returningDestination={inboundInfo && inboundInfo.destinationUIC}
          stations={stations}
          travelDates={{ outboundDate, inboundDate }}
          onSubmit={handleSearchChange}
          subscriptionStartDate={journeySearchState.subscriptionStartDate}
          subscriptionEndDate={journeySearchState.subscriptionEndDate}
          isExchange={false}
        />
      </Modal>

      <Modal isOpen={showNotEnoughPoints} onClose={toggleNotEnoughPointsModal}>
        <FormattedMessage id="journeySummary.notEnoughPoints" />
      </Modal>

      {isMember && productFamilies.includes(productFamilyCodes.EUROSTAR_LOYALTY) && (
        <Row span={12}>
          <Grid>
            <AvailablePointsCell>
              <ClubEurostarImg />
            </AvailablePointsCell>
            <AvailablePointsCell>
              <FormattedMessage id="clubEurostar.activePoints" />
            </AvailablePointsCell>
            <AvailablePointsBoldCell>{activePoints}</AvailablePointsBoldCell>
          </Grid>
        </Row>
      )}

      <Row span={12}>
        <JourneySearchHeader
          outbound={outboundInfo}
          inbound={inboundInfo}
          toggleSearchMenu={toggleSearchMenu}
          cheapestFares={cheapestFares}
          currency={currency}
          subscriptionStartDate={journeySearchState.subscriptionStartDate}
          subscriptionEndDate={journeySearchState.subscriptionEndDate}
        />
      </Row>

      <Row>
        <JourneySearchTables
          lowestFareSearch={
            new URLSearchParams(search).get('lowestFareFinder') === 'true'
              ? {
                  outbound: {
                    price: Number(new URLSearchParams(search).get('outboundLowestPrice')),
                    classOfService: new URLSearchParams(search).get('outboundLowestClass')
                  },
                  inbound: {
                    price: Number(new URLSearchParams(search).get('inboundLowestPrice')),
                    classOfService: new URLSearchParams(search).get('inboundLowestClass')
                  }
                }
              : undefined
          }
          shouldUseSelection
          outboundDate={outboundDate}
          inboundDate={inboundDate}
          isGroupBooking={isGroupBooking}
          outboundInfo={outboundInfo}
          inboundInfo={inboundInfo}
          outboundClassesOfService={journeyOutbound?.classesOfService}
          inboundClassesOfService={journeyInbound?.classesOfService}
          isEvacSearch={isEvacSearch}
          isUnassignedSearch={isUnassignedSearch}
          outboundJourneys={buildOutboundJourneys({
            data,
            outboundDate,
            inboundDate,
            selectedInbound: inbound
          })}
          inboundJourneys={buildInboundJourneys({
            data,
            outboundDate,
            inboundDate,
            selectedOutbound: outbound
          })}
          selectFare={selectFare}
          selectedOutbound={outbound}
          selectedInbound={inbound}
          currency={currency}
          cheapestFares={cheapestFares}
          isWheelchairSearch={isWheelchairSearch}
          dogOnlySearch={dogOnlySearch}
          requestedProductFamilies={productFamilies}
          passengers={passengers}
          animalsPerTrain={animalsPerTrain}
          animalsPerTrainLoading={animalsPerTrainLoading}
          serviceDetailsData={serviceDetailsData}
          serviceDetailsLoading={serviceDetailsLoading}
          selectedProductFamily={selectedProductFamily}
          assistancePerTrain={assistancePerTrain}
          assistancePerTrainLoading={assistancePerTrainLoading}
          isEsUser={isEsUser}
        />
      </Row>

      {hasJourneys && (
        <>
          <JourneyDigest
            autoExpand={isDesktop ? true : false}
            outbound={buildLegsFromFare(outbound)}
            outboundTotalPrice={outboundTotalPrice}
            outboundTotalPoints={outboundTotalPoints}
            inbound={buildLegsFromFare(inbound)}
            inboundTotalPrice={inboundTotalPrice}
            inboundTotalPoints={inboundTotalPoints}
            totalPrice={totalPrice}
            isReturnJourney={isReturnJourney}
            passengers={passengers}
            currency={currency}
            offlineFee={offlineFee}
            infantCount={getInfantCountFromSearch(search)}
            isLoyaltyBooking={isLoyaltyBooking}
            confirmButton={
              <ConfirmButton
                loading={loading}
                disabled={isSubmitDisabled()}
                type="submit"
                onClick={handleSubmit({ isProvisionalDefault: false })}
                data-cy="create-provisional"
                variant="primary"
              >
                <FormattedMessage id="journeySummary.reserverSeats" />
                <ChevronRight />
              </ConfirmButton>
            }
            confirmProvisionalButton={
              hasDBOrNSOrLyriaOrSNCFLeg() ? (
                <ConfirmButton
                  disabled={isSubmitDisabled()}
                  type="submit"
                  onClick={handleSubmit({ isProvisionalDefault: true })}
                  data-cy="create-default-provisional"
                  variant="secondary"
                >
                  <FormattedMessage id="journeySummary.setDifferentTrains" />
                </ConfirmButton>
              ) : null
            }
            confirmError={loyaltyError}
            originUIC={searchParams.origin}
            destinationUIC={searchParams.destination}
          />
        </>
      )}
    </StyledPage>
  );
};

JourneySearch.propTypes = {
  history: historyShape,
  data: PropTypes.object,
  error: PropTypes.object,
  loading: PropTypes.bool,
  searchParams: searchParamsShape,
  createProvisionalBooking: PropTypes.func,
  location: locationShape,
  stations: stationsShape,
  client: clientShape,
  membershipNumber: PropTypes.string,
  cheapestFares: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      price: PropTypes.number
    })
  ),
  user: userShape,
  animalsPerTrain: PropTypes.shape({
    outbound: PropTypes.arrayOf(PropTypes.object),
    inbound: PropTypes.arrayOf(PropTypes.object)
  }),
  animalsPerTrainLoading: PropTypes.shape({
    outbound: PropTypes.bool,
    inbound: PropTypes.bool
  }),
  serviceDetailsData: PropTypes.shape({
    outbound: PropTypes.arrayOf(PropTypes.object),
    inbound: PropTypes.arrayOf(PropTypes.object)
  }),
  serviceDetailsLoading: PropTypes.shape({
    outbound: PropTypes.bool,
    inbound: PropTypes.bool
  }),
  assistancePerTrain: PropTypes.shape({
    outbound: PropTypes.arrayOf(PropTypes.object),
    inbound: PropTypes.arrayOf(PropTypes.object)
  }),
  assistancePerTrainLoading: PropTypes.shape({
    outbound: PropTypes.bool,
    inbound: PropTypes.bool
  }),
  seniorsAges: PropTypes.array,
  youthAges: PropTypes.array,
  childAges: PropTypes.array,
  isDesktop: PropTypes.bool
};

export default JourneySearch;
